import {
  Component,
  ElementRef,
  Inject,
  inject,
  Optional,
  QueryList,
  ViewChildren
} from "@angular/core";
import { ContainerLoadFn } from "src/app/components/ui/container/container-base/container-base.component";
import { forkJoin, from } from "rxjs";
import { delay, map, switchMap } from "rxjs/operators";
import { ReportService } from "src/app/services/report.service";
import { soapTemplateWithHealthRecordoToTemplateItems } from "src/app/support/health-record";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { ServiceBackend } from "src/app/backend/api/service.backend";
import { TemplateService } from "src/app/services/template.service";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { HealthRecordBackend } from "src/app/backend/api/health-record.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { TicketBackend } from "src/app/backend/api/ticket.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import {
  HealthRecord,
  HealthRecordAttachment,
  HealthRecordDetailSOAP
} from "src/app/backend/models/health-record";
import { findIndex, first } from "lodash-es";
import { SoapTemplate } from "src/app/backend/models/soap-template";
import { Configuration } from "src/app/backend/models/configuration";
import { Customer } from "src/app/backend/models/customer/customer";
import { Pet } from "src/app/backend/models/pet/pet";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { ReportDesignItemConfig } from "src/app/modules/bootstrap/modules/report/report-design-item";
import {
  buildSoapDesignModel,
  SoapDesign
} from "src/app/backend/models/soap/soap-design";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";

type ClinicalCaseData = {
  soapTemplate: SoapTemplate;
  configuration: Configuration;
  healthRecord: HealthRecord<HealthRecordDetailSOAP>;
  healthRecordAttachments: any[];
  customer: Customer;
  patient: Pet;
};

type ClinicalCaseItem = {
  healthRecord: HealthRecord<HealthRecordDetailSOAP>;
  healthRecordAttachments: HealthRecordAttachment[];
  currentTemplate: ReportDesignItemConfig[];
  soapDesignDataSource: SoapDesign;
};

type LoadData = Array<ClinicalCaseData>;

@Component({
  selector: "snt-report-preview",
  templateUrl: "report-preview.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  standalone: false
})
export class ReportPreviewComponent {
  @ViewChildren("scrollAnchor")
  scrollAnchors!: QueryList<ElementRef<HTMLDivElement>>;

  loadFn: ContainerLoadFn<LoadData>;

  clinicalCaseItems: ClinicalCaseItem[] = [];

  readonly memberBackend = inject(MemberBackend);
  private readonly templateService = inject(TemplateService);
  private readonly soapTemplateBackend = inject(SoapTemplateBackend);
  private readonly healthRecordBackend = inject(HealthRecordBackend);
  private readonly reportService = inject(ReportService);
  private readonly patientBackend = inject(PetBackend);
  private readonly ticketBackend = inject(TicketBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly configurationBackend = inject(ConfigurationBackend);
  private readonly physicianBackend = inject(PhysicianBackend);

  constructor(
    @Optional()
    @Inject("sidespace-data")
    private sideSpaceData: {
      masterCaseHealthRecordId: string;
      healthRecordId: string;
    }
  ) {
    const masterCaseHealthRecordId = sideSpaceData.masterCaseHealthRecordId;
    const healthRecordId = sideSpaceData.healthRecordId;

    this.loadFn = () => {
      return this.healthRecordBackend.load(masterCaseHealthRecordId).pipe(
        switchMap((masterCaseHealthRecord) => {
          return this.healthRecordBackend
            .listAllChildrenByPatient(
              masterCaseHealthRecord.patient.id,
              masterCaseHealthRecordId
            )
            .pipe(
              map((childrenHealthRecords) => {
                return [masterCaseHealthRecord, ...childrenHealthRecords];
              })
            );
        }),
        switchMap((healthRecords) => {
          return forkJoin(
            healthRecords.map((healthRecord) => {
              return forkJoin({
                healthRecordSoapTemplateConfiguration: this.healthRecordBackend
                  .load(healthRecord.id)
                  .pipe(
                    switchMap((healthRecord) => {
                      return forkJoin([
                        this.soapTemplateBackend
                          .load(
                            (<HealthRecord<HealthRecordDetailSOAP>>healthRecord)
                              .detail!.templateId
                          )
                          .pipe(
                            map((soapTemplate) => {
                              return {
                                healthRecord,
                                soapTemplate
                              };
                            })
                          ),
                        this.configurationBackend.loadPms()
                      ]).pipe(
                        map(
                          ([{ healthRecord, soapTemplate }, configuration]) => {
                            return {
                              healthRecord,
                              soapTemplate,
                              configuration
                            };
                          }
                        )
                      );
                    })
                  ),
                attachments:
                  this.healthRecordBackend.loadAttachments(healthRecordId)
              }).pipe(
                switchMap(
                  ({ healthRecordSoapTemplateConfiguration, attachments }) => {
                    return this.patientBackend
                      .load({
                        id: healthRecordSoapTemplateConfiguration.healthRecord
                          .patient.id
                      })
                      .pipe(
                        switchMap((patient) =>
                          this.customerBackend
                            .load(
                              patient.owner?.id || first(patient.relatives)!.id
                            )
                            .pipe(
                              map((customer) => ({
                                customer,
                                patient
                              }))
                            )
                        )
                      )
                      .pipe(
                        map(({ patient, customer }) => {
                          return {
                            healthRecord: <HealthRecord>(
                              healthRecordSoapTemplateConfiguration.healthRecord
                            ),
                            healthRecordAttachments: attachments,
                            soapTemplate:
                              healthRecordSoapTemplateConfiguration.soapTemplate,
                            configuration:
                              healthRecordSoapTemplateConfiguration.configuration,
                            patient,
                            customer
                          };
                        })
                      );
                  }
                )
              );
            })
          );
        })
      );
    };
  }

  onData(data: LoadData): void {
    this.clinicalCaseItems = data.map((clinicCaseData) => {
      return {
        healthRecord: clinicCaseData.healthRecord,
        healthRecordAttachments: clinicCaseData.healthRecordAttachments,
        currentTemplate: soapTemplateWithHealthRecordoToTemplateItems(
          clinicCaseData.soapTemplate,
          <HealthRecord>clinicCaseData.healthRecord,
          clinicCaseData.healthRecordAttachments,
          this.memberBackend,
          this.physicianBackend,
          this.templateService,
          this.patientBackend,
          this.customerBackend,
          clinicCaseData.customer,
          clinicCaseData.patient,
          {
            readOnly: true,
            hideCustomerAndPatientData: true
          }
        ),
        soapDesignDataSource: buildSoapDesignModel(
          clinicCaseData.healthRecord,
          clinicCaseData.customer,
          clinicCaseData.patient
        )
      };
    });

    const index = findIndex(
      this.clinicalCaseItems,
      (x) => x.healthRecord.id === this.sideSpaceData.healthRecordId
    );

    if (index > 0) {
      setTimeout(() => {
        const scrollAnchorElement = this.scrollAnchors.get(index - 1);
        scrollAnchorElement?.nativeElement.scrollIntoView({
          behavior: "smooth"
        });
      }, 750);
    }
  }
}
